import React, { useMemo, useState, useEffect, useCallback } from 'react';
import './CategoryPickerModal.css';

/**
 * CategoryPickerModal
 *
 * A self-contained category selector: renders an input-like trigger that opens a
 * large (3/4 screen) modal. The modal lists the MAIN (top-level) categories;
 * clicking a main category expands it to reveal its sub-categories. Selecting a
 * sub-category (or a main category via its "Select" button) sets the value and
 * closes. Typing in the search box switches to a flat filtered list.
 *
 * Props:
 *   categories  — flat list of { id, name, ParentCategoryId }
 *   value       — selected category id
 *   onChange    — (id) => void   ('' to clear)
 *   placeholder — trigger text when nothing is selected
 *   title       — modal heading
 *   hideTrigger — when true, the input-like trigger is not rendered (the parent
 *                 opens the modal itself, e.g. from its own button)
 *   open        — optional controlled open state. When provided the parent drives
 *                 open/close; when omitted the component manages it internally
 *                 (default behaviour, unchanged for existing callers).
 *   onOpenChange— (bool) => void, fired whenever the open state should change
 */
function buildTree(categories) {
  const byId = new Map(categories.map((c) => [c.id, c]));
  const childrenOf = new Map();
  const roots = [];
  for (const c of categories) {
    const pid = c.ParentCategoryId && byId.has(c.ParentCategoryId) ? c.ParentCategoryId : null;
    if (pid) {
      if (!childrenOf.has(pid)) childrenOf.set(pid, []);
      childrenOf.get(pid).push(c);
    } else {
      roots.push(c);
    }
  }
  // Sort by Priority (lower first; nulls last), falling back to name as a tiebreaker.
  const byPriority = (a, b) => {
    const pa = a.Priority == null ? Number.MAX_SAFE_INTEGER : Number(a.Priority);
    const pb = b.Priority == null ? Number.MAX_SAFE_INTEGER : Number(b.Priority);
    if (pa !== pb) return pa - pb;
    return String(a.name || a.id).localeCompare(String(b.name || b.id));
  };
  roots.sort(byPriority);
  for (const arr of childrenOf.values()) arr.sort(byPriority);
  return { byId, childrenOf, roots };
}

const CategoryPickerModal = ({
  categories = [],
  value,
  onChange,
  placeholder = 'Select a category…',
  title = 'Select Category',
  hideTrigger = false,
  open: controlledOpen,
  onOpenChange,
}) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState(() => new Set());

  const { byId, childrenOf, roots } = useMemo(() => buildTree(categories), [categories]);
  const selectedName = value ? byId.get(value)?.name || value : '';
  const term = search.trim().toLowerCase();

  const setOpen = useCallback(
    (next) => {
      if (!isControlled) setInternalOpen(next);
      if (onOpenChange) onOpenChange(next);
    },
    [isControlled, onOpenChange],
  );

  const close = useCallback(() => {
    setOpen(false);
    setSearch('');
  }, [setOpen]);

  const select = (id) => {
    onChange(id);
    close();
  };

  const toggle = (id) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') close();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, close]);

  const renderNode = (node, depth) => {
    const kids = childrenOf.get(node.id) || [];
    const isSelected = value === node.id;
    const pad = 16 + depth * 20;

    if (kids.length) {
      const isOpen = expanded.has(node.id);
      return (
        <div className="cpm-group" key={node.id}>
          <div className={`cpm-row cpm-row--parent${isSelected ? ' cpm-row--selected' : ''}`}>
            <button
              type="button"
              className="cpm-row__main"
              style={{ paddingLeft: pad }}
              onClick={() => toggle(node.id)}
            >
              <span className={`cpm-chevron${isOpen ? ' cpm-chevron--open' : ''}`}>▶</span>
              <span className="cpm-row__name">{node.name || node.id}</span>
              <span className="cpm-count">{kids.length}</span>
            </button>
            <button type="button" className="cpm-select-btn" onClick={() => select(node.id)}>
              Select
            </button>
          </div>
          {isOpen && kids.map((k) => renderNode(k, depth + 1))}
        </div>
      );
    }

    return (
      <button
        type="button"
        key={node.id}
        className={`cpm-row cpm-row--leaf${isSelected ? ' cpm-row--selected' : ''}`}
        style={{ paddingLeft: pad }}
        onClick={() => select(node.id)}
      >
        {isSelected && <span className="cpm-check">✓</span>}
        <span className="cpm-row__name">{node.name || node.id}</span>
      </button>
    );
  };

  const flatFiltered = term
    ? categories
        .filter((c) => String(c.name || c.id).toLowerCase().includes(term))
        .sort((a, b) => String(a.name || a.id).localeCompare(String(b.name || b.id)))
    : [];

  return (
    <>
      {!hideTrigger && (
        <div
          className={`cpm-trigger${value ? '' : ' cpm-trigger--empty'}`}
          role="button"
          tabIndex={0}
          onClick={() => setOpen(true)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setOpen(true);
            }
          }}
        >
          <span className="cpm-trigger__label">{selectedName || placeholder}</span>
          <span className="cpm-trigger__icon">🔍</span>
        </div>
      )}

      {open && (
        <div className="cpm-overlay" onClick={close}>
          <div className="cpm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="cpm-header">
              <h3 className="cpm-title">{title}</h3>
              <button type="button" className="cpm-close" onClick={close} aria-label="Close">
                ×
              </button>
            </div>

            <div className="cpm-search">
              <input
                autoFocus
                type="text"
                value={search}
                placeholder="Search categories…"
                onChange={(e) => setSearch(e.target.value)}
              />
              <div className="cpm-search__count">
                {term
                  ? `${flatFiltered.length} match${flatFiltered.length === 1 ? '' : 'es'}`
                  : `${categories.length} categories · ${roots.length} main`}
              </div>
            </div>

            <div className="cpm-body">
              {term ? (
                flatFiltered.length === 0 ? (
                  <div className="cpm-empty">No categories match “{search}”.</div>
                ) : (
                  flatFiltered.map((c) => (
                    <button
                      type="button"
                      key={c.id}
                      className={`cpm-row cpm-row--leaf${value === c.id ? ' cpm-row--selected' : ''}`}
                      onClick={() => select(c.id)}
                    >
                      {value === c.id && <span className="cpm-check">✓</span>}
                      <span className="cpm-row__name">{c.name || c.id}</span>
                    </button>
                  ))
                )
              ) : roots.length === 0 ? (
                <div className="cpm-empty">No categories available.</div>
              ) : (
                roots.map((r) => renderNode(r, 0))
              )}
            </div>

            <div className="cpm-footer">
              {value ? (
                <button type="button" className="cpm-clear" onClick={() => onChange('')}>
                  ✕ Clear selection
                </button>
              ) : (
                <span />
              )}
              <button type="button" className="cpm-done" onClick={close}>
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CategoryPickerModal;
